import * as SwitchPrimitive from "@radix-ui/react-switch";
import type { ReactNode } from "react";
import { useId } from "react";
import cx from "ui/cx";
import Label from "ui/forms/Label";
import styles from "./Switch.module.scss";

export interface SwitchProps extends Omit<React.ComponentProps<typeof SwitchPrimitive.Root>, "asChild"> {
  label?: ReactNode;
  className?: string;
  onChangeBoolean?: (checked: boolean) => void;
  tooltip?: ReactNode;
}

export default function Switch({
  label,
  disabled = false,
  className,
  checked,
  onChangeBoolean, // use for boolean state only
  onCheckedChange, // use for full state compatibility
  tooltip,
  ...props
}: SwitchProps) {
  const id = useId();
  const isDisabled = disabled;

  const handleCheckedChange = (newChecked: boolean) => {
    if (!isDisabled) {
      onCheckedChange?.(newChecked);
      if (!onCheckedChange) {
        onChangeBoolean?.(newChecked);
      }
    }
  };

  const controlled = !onCheckedChange && !onChangeBoolean;

  return (
    <div
      className={cx(
        styles.wrapper,
        isDisabled && styles.disabledBase,
        !isDisabled && !controlled && styles.clickable,
        disabled && styles.disabled,
        className,
      )}
      data-testid="switch-wrapper"
    >
      <SwitchPrimitive.Root
        id={id}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        disabled={isDisabled}
        className={styles.root}
        data-testid="switch-input"
        {...props}
      >
        <SwitchPrimitive.Thumb className={styles.thumb} data-testid="switch-thumb" />
      </SwitchPrimitive.Root>
      {label && (
        <Label
          // if controlled (no onChange handlers), don't set the htmlFor attribute so that the label is not clickable
          htmlFor={!controlled ? id : undefined}
          disabled={isDisabled}
          data-testid="switch-label"
          tooltip={tooltip}
        >
          {label}
        </Label>
      )}
    </div>
  );
}
